import { useState, type ReactNode } from 'react';
import {
  PAR, decrirePaliers, euros, mentionTva, montantPour, type Periodicite, type Tarif,
} from '../lib/prix.ts';
import {
  Bouton, Carte, Champ, Etiquette, GroupeBoutons, Message,
} from '@journal/ui';

/**
 * Choix d'une offre, et souscription.
 *
 * ── Ce qui manquait ─────────────────────────────────────────────────────────
 * `POST /billing/checkout-session` existait depuis le début, et rien ne
 * l'appelait : l'écran Abonnement ne proposait que « Gérer le paiement », qui
 * ouvre le portail Stripe — lequel exige un abonnement déjà souscrit. Une école
 * en période d'essai n'avait donc aucun moyen d'acheter quoi que ce soit depuis
 * l'application.
 *
 * ── Le nombre de comptes est une décision, pas un réglage ───────────────────
 * Pour l'offre École, c'est LA question posée au directeur : combien
 * d'enseignants. Elle est donc au premier plan de la carte, avec son plancher
 * expliqué — on ne peut pas acheter moins de sièges qu'il n'y a de comptes déjà
 * ouverts, sous peine d'en couper au renouvellement.
 */

export interface OffrePublique {
  code: string;
  name: string;
  limits: Record<string, unknown>;
  features: string[];
  /**
   * Sièges au-dessous desquels l'offre ne se vend pas.
   *
   * Vient du serveur, qui relève toute demande inférieure. Le deviner ici
   * laissait choisir un compte, afficher « 1 », et Stripe en facturait deux —
   * l'écart ne se voyait qu'à la première facture.
   */
  siegesMinimum: number;
  /**
   * Périodicités réellement vendables, déduites des tarifs Stripe renseignés.
   *
   * Le sélecteur proposait « Mensuel » et « Annuel » quoi qu'il arrive. Quand
   * aucun tarif annuel n'existe — ce qui a été le cas tout le temps où seuls
   * les tarifs mensuels étaient créés — le client choisissait l'annuel, était
   * envoyé vers le paiement, et recevait « Cette offre n'est pas disponible
   * dans cette périodicité ». Proposer une porte qui se referme est la pire
   * façon de vendre.
   *
   * Optionnelle : une réponse d'API plus ancienne ne la porte pas, et le
   * mensuel reste alors le seul choix — le repli le plus sûr.
   */
  periodicites?: Array<'MONTH' | 'YEAR'>;
  /**
   * Les prix, lus chez Stripe par le serveur. Une périodicité absente n'a pas
   * pu être lue : l'interface le dit au lieu d'inventer un montant.
   * Optionnels : une réponse d'API plus ancienne ne les porte pas.
   */
  tarifs?: Partial<Record<Periodicite, Tarif>>;
}

/** Fonctionnalité `equipe` : c'est elle qui fait d'une offre une offre à sièges. */
export function vendDesSieges(offre: OffrePublique): boolean {
  return offre.features.includes('equipe');
}

/**
 * Sièges proposés par défaut.
 *
 * Jamais moins que les comptes déjà ouverts : proposer « 5 » à une école qui en
 * a huit ferait acheter une réduction d'effectif sans le dire.
 */
export function siegesInitiaux(offre: OffrePublique, comptesOuverts: number): number {
  const defaut = Number(offre.limits['maxUsers'] ?? 5);
  return Math.max(
    Number.isFinite(defaut) && defaut > 0 ? defaut : 5,
    comptesOuverts,
    offre.siegesMinimum,
    1,
  );
}

const LIBELLE_FONCTION: Record<string, string> = {
  journal: 'Cahier journal',
  progressions: 'Progressions',
  programmation: 'Programmation',
  edt: 'Emploi du temps',
  evaluation: 'Évaluation par compétences',
  export_pdf: 'Impression et export',
  equipe: 'Gestion des comptes de l’équipe',
};

/** Le prix d'une offre dans la périodicité choisie, et son total. */
function PrixChoisi({
  tarif, periodicite, aSieges, quantite,
}: { tarif: Tarif | undefined; periodicite: Periodicite; aSieges: boolean; quantite: number | null }) {
  if (!tarif) {
    return (
      <p className="text-[13px] text-[var(--color-fg-muted)]">
        Le prix s’affiche sur la page de paiement, avant toute validation.
      </p>
    );
  }
  const total = quantite !== null ? montantPour(tarif, quantite) : null;
  const paliers = decrirePaliers(tarif);
  return (
    <div
      data-testid="prix-choisi"
      className="rounded-lg bg-[var(--color-surface-raised)] px-3 py-2.5 text-[13px] leading-relaxed"
    >
      {paliers && <p className="text-[var(--color-fg-muted)]">{`${paliers}, ${PAR[periodicite]}.`}</p>}
      {total !== null ? (
        <p>
          {aSieges ? 'Total : ' : ''}
          <strong className="text-[16px]">{euros(total, tarif.devise)}</strong>
          {` ${PAR[periodicite]}${aSieges && quantite ? ` pour ${quantite} comptes` : ''}`}
        </p>
      ) : (
        <p className="text-[var(--color-fg-muted)]">Le total s’affiche dès que le nombre de comptes est indiqué.</p>
      )}
      <p className="text-[12px] text-[var(--color-fg-muted)]">{mentionTva()}</p>
    </div>
  );
}

export function ChoixOffre({
  offres, comptesOuverts, estProprietaire, surSouscription, renonciationAcceptee, sousLesOffres,
  titre = 'Choisir une offre',
}: {
  /** « Reprendre votre abonnement » quand l'espace en avait un, terminé. */
  titre?: string;
  /** Affiché SOUS les boutons « Souscrire » : la case d'exécution immédiate. */
  sousLesOffres?: ReactNode;
  offres: OffrePublique[];
  /** Comptes déjà ouverts dans l'école : c'est le plancher des sièges. */
  comptesOuverts: number;
  /** Seul un propriétaire peut engager une dépense — le serveur l'exige aussi. */
  estProprietaire: boolean;
  /**
   * Le client a-t-il demandé l'exécution immédiate du service ?
   *
   * Le service commençant sur-le-champ, la loi le fait renoncer à ses quatorze
   * jours de rétractation — mais seulement s'il l'a expressément demandé. Tant
   * que la case n'est pas cochée, souscrire reste impossible : le serveur
   * refuse de son côté, et laisser le bouton actif ne produirait qu'une erreur
   * incompréhensible.
   */
  renonciationAcceptee: boolean;
  surSouscription: (
    offre: OffrePublique, periodicite: 'MONTH' | 'YEAR', sieges?: number,
  ) => Promise<void>;
}) {
  const [periodicite, setPeriodicite] = useState<'MONTH' | 'YEAR'>('MONTH');
  /*
   * Une CHAÎNE, et non un nombre.
   *
   * Le champ part vide : « combien d'enseignants » est la question posée au
   * directeur, et une valeur pré-remplie y répond à sa place. Cinq était le
   * défaut ; un directeur pressé achetait donc cinq sièges sans l'avoir
   * décidé.
   *
   * Vide n'est pas un nombre — ni zéro, ni le plancher. Le distinguer impose
   * de garder la saisie telle quelle, et de refuser la souscription tant
   * qu'elle n'a pas eu lieu.
   */
  const [sieges, setSieges] = useState<Record<string, string>>({});
  const [enCours, setEnCours] = useState<string | null>(null);

  if (offres.length === 0) return null;

  /*
   * Les périodicités PROPOSÉES sont celles que toutes les offres acceptent.
   *
   * L'intersection, et non l'union : le sélecteur est commun aux deux cartes,
   * et afficher « Annuel » parce qu'une seule des deux l'accepte enverrait
   * l'autre dans le mur. Une offre sans périodicité déclarée — réponse d'API
   * plus ancienne — est réputée mensuelle, le repli le plus sûr.
   */
  const periodicitesOffertes = (['MONTH', 'YEAR'] as const).filter((p) =>
    offres.every((o) => (o.periodicites ?? ['MONTH']).includes(p)));

  const choix: Array<'MONTH' | 'YEAR'> =
    periodicitesOffertes.length > 0 ? [...periodicitesOffertes] : ['MONTH'];
  // Le choix courant peut être devenu impossible : on retombe sur le premier.
  const periodiciteRetenue = choix.includes(periodicite) ? periodicite : choix[0]!;

  return (
    <Carte
      titre={titre}
      description="Le paiement est traité par notre prestataire ; aucune donnée bancaire ne transite par cette application."
      action={
        /*
         * Un seul choix possible : pas de sélecteur du tout. Un groupe de
         * boutons à une option laisse croire qu'il en existe une autre.
         */
        choix.length > 1 ? (
          <GroupeBoutons
            legende="Périodicité"
            valeur={periodiciteRetenue}
            surChangement={setPeriodicite}
            options={choix.map((p) => ({
              valeur: p,
              texte: p === 'MONTH' ? 'Mensuel' : 'Annuel',
            }))}
          />
        ) : undefined
      }
    >
      {!estProprietaire && (
        <div className="mb-4">
          <Message ton="info" titre="Réservé au propriétaire du compte">
            Seul le propriétaire peut souscrire ou changer d'offre. Demandez-lui
            de s'en charger depuis son propre accès.
          </Message>
        </div>
      )}

      {/*
        * ── Les offres s'alignent rangée par rangée ───────────────────────────
        * Chaque offre occupe six rangées de la même grille (sous-grille) :
        * titre, fonctions, nombre de comptes, prix, message, bouton. L'École
        * seule demande un nombre de comptes : sans cet alignement, son prix et
        * son bouton glissaient vers le bas, et plus rien ne se lisait en face.
        * La rangée reste donc présente, vide, pour l'offre Enseignant.
        */}
      <div className="grid gap-4 sm:grid-cols-2">
        {offres.map((offre) => {
          const aSieges = vendDesSieges(offre);
          const saisie = sieges[offre.code] ?? '';
          const valeur = saisie === '' ? null : Number(saisie);
          /*
           * Deux planchers, et le plus haut gagne : les comptes déjà ouverts,
           * pour ne pas vendre une réduction d'effectif sans le dire ; et le
           * minimum de l'offre, parce que l'École s'adresse à une équipe et
           * que son tarif facture les deux premiers sièges d'un bloc.
           */
          const plancher = Math.max(comptesOuverts, offre.siegesMinimum, 1);

          /*
           * Ce qui empêche de souscrire, et POURQUOI — la même phrase servant
           * au bouton et au champ.
           *
           * Un bouton désactivé sans raison visible se lit comme une panne :
           * on reclique, puis on écrit au support. Dire ce qui manque coûte
           * une ligne.
           */
          const blocage = !aSieges || valeur === null
            ? (aSieges ? 'Indiquez le nombre de comptes.' : null)
            : !Number.isFinite(valeur) || valeur < plancher
              ? `Au moins ${plancher} comptes.`
              : valeur > 500
                ? 'Au-delà de 500 comptes, écrivez-nous : cela se négocie.'
                : null;

          return (
            <section
              key={offre.code}
              className="row-span-6 grid grid-rows-subgrid gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
            >
              <header className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-[15px] font-semibold">{offre.name}</h3>
                {aSieges && <Etiquette ton="info">Plusieurs comptes</Etiquette>}
              </header>

              <ul className="flex flex-col gap-1 text-[13px] text-[var(--color-fg-muted)]">
                {offre.features.map((f) => (
                  <li key={f} className="flex items-baseline gap-2">
                    <span aria-hidden="true">·</span>
                    {LIBELLE_FONCTION[f] ?? f}
                  </li>
                ))}
              </ul>

              <div>
              {aSieges && (
                <Champ
                  type="number"
                  libelle="Nombre de comptes"
                  min={plancher}
                  max={500}
                  step={1}
                  value={saisie}
                  placeholder={String(plancher)}
                  onChange={(e) =>
                    setSieges((s) => ({ ...s, [offre.code]: e.target.value }))
                  }
                  aide={
                    comptesOuverts > offre.siegesMinimum
                      ? `Au moins ${plancher} : c'est le nombre de comptes déjà ouverts.`
                      : `Au moins ${plancher} : cette offre s'adresse à une équipe.`
                        + ' Un compte par enseignant, modifiable ensuite.'
                  }
                  disabled={!estProprietaire}
                />
              )}
              </div>

              {/*
                * Le prix, et pour une offre à sièges le TOTAL au nombre de
                * comptes saisi : les CGV promettent que « le total est affiché
                * avant tout paiement ». Il ne l'était que sur la page Stripe,
                * après avoir quitté l'application.
                */}
              <PrixChoisi
                tarif={offre.tarifs?.[periodiciteRetenue]}
                periodicite={periodiciteRetenue}
                aSieges={aSieges}
                quantite={aSieges
                  ? (valeur !== null && Number.isFinite(valeur) && valeur >= plancher && valeur <= 500 ? valeur : null)
                  : 1}
              />

              <div className="self-end">
              {blocage && estProprietaire && renonciationAcceptee && (
                <p className="text-[13px] text-[var(--color-fg-muted)]">{blocage}</p>
              )}
              {/* Un bouton grisé dit POURQUOI : sinon on le croit en panne. */}
              {estProprietaire && !renonciationAcceptee && (
                <p className="text-[13px] font-semibold text-[var(--color-fg-muted)]">
                  Cochez la case ci-dessous pour souscrire.
                </p>
              )}
              </div>

              <Bouton
                variante="primary"
                disabled={!estProprietaire || !renonciationAcceptee || blocage !== null}
                chargement={enCours === offre.code}
                onClick={() => {
                  /*
                   * `valeur` ne peut pas être nulle ici : le bouton est
                   * désactivé tant qu'elle l'est. La garde reste, parce
                   * qu'elle coûte une ligne et qu'un envoi sans quantité
                   * retomberait sur le défaut du serveur — cinq sièges que
                   * personne n'a demandés.
                   */
                  if (aSieges && valeur === null) return;
                  setEnCours(offre.code);
                  void surSouscription(
                    offre, periodiciteRetenue,
                    aSieges ? Math.max(valeur!, plancher) : undefined,
                  ).finally(() => setEnCours(null));
                }}
              >
                {`Souscrire à l’offre ${offre.name}`}
              </Bouton>
            </section>
          );
        })}
      </div>
      {sousLesOffres && <div className="mt-5 border-t border-[var(--color-border)] pt-4">{sousLesOffres}</div>}
    </Carte>
  );
}
